import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FormattedMessage, injectIntl } from 'react-intl';

import GenericDialog from '../GenericDialog';
import ContentBox from '../layout/ContentBox';
import { ExtendedEnrollment } from '../../views/EvaluationDialog';
import { convertToQid } from '../../utilities/stringHelper';

type Props = {
  isOpen: boolean;
  closeDialog: () => void;
  enrollments: ExtendedEnrollment[] | undefined;
  onSendEmail: (payload: { enrollmentIds: number[] }) => void | Promise<void>;
  intl: {
    formatMessage: (descriptor: { id: string }) => string;
  };
};

const displayName = (firstname?: string, surname?: string): string => {
  const parts = [firstname?.trim(), surname?.trim()].filter(Boolean);
  return parts.join(' ');
};

/** Institute notification forward emails (see Institute.forwardAddresses). */
const formatForwardAddresses = (forwardAddresses?: string[] | null): string => {
  if (!forwardAddresses?.length) {
    return '';
  }
  return forwardAddresses.map((a) => a?.trim()).filter(Boolean).join(', ');
};

function RegisteredUsersNotifyDialog({
  isOpen,
  closeDialog,
  enrollments,
  onSendEmail,
  intl,
}: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds(new Set());
      return;
    }
    if (!enrollments) {
      return;
    }
    const approved = enrollments.filter((e) => e.status === 'approved');
    if (approved.length === 0) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(approved.map((e) => e.id)));
  }, [isOpen, enrollments]);

  const toggleId = (enrollmentId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(enrollmentId)) {
        next.delete(enrollmentId);
      } else {
        next.add(enrollmentId);
      }
      return next;
    });
  };

  const loading = enrollments === undefined;
  const approvedList = enrollments?.filter((e) => e.status === 'approved') ?? [];
  const empty = !loading && approvedList.length === 0;
  const list = approvedList;
  const selectedInList = list.filter((e) => selectedIds.has(e.id)).length;
  const allSelectedInList = list.length > 0 && selectedInList === list.length;
  const someSelectedInList = selectedInList > 0 && selectedInList < list.length;

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const allSelected = list.length > 0 && list.every((e) => prev.has(e.id));
      if (allSelected) {
        return new Set();
      }
      return new Set(list.map((e) => e.id));
    });
  };

  const handleSend = async () => {
    await onSendEmail({ enrollmentIds: Array.from(selectedIds) });
  };

  return (
    <GenericDialog isOpen={isOpen} onRequestClose={closeDialog} maxWidth="lg">
      <ContentBox
        show
        title={<FormattedMessage id="internalTrial.notifyRegisteredUsers" />}
      >
        <Box
          sx={{
            minHeight: 200,
            maxHeight: 'min(60vh, 480px)',
            overflow: 'auto',
          }}
        >
          {loading && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                py: 4,
              }}
            >
              <CircularProgress size={28} />
              <Typography color="text.secondary">
                <FormattedMessage id="internalTrial.notifyRegisteredUsersLoading" />
              </Typography>
            </Box>
          )}
          {empty && (
            <Typography sx={{ py: 2 }} color="text.secondary">
              <FormattedMessage id="internalTrial.notifyNoRegisteredUsers" />
            </Typography>
          )}
          {!loading && !empty && (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someSelectedInList}
                        checked={allSelectedInList}
                        onChange={toggleSelectAll}
                        inputProps={{
                          'aria-label': intl.formatMessage({ id: 'internalTrial.notifySelectAll' }),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="internalTrial.notifyColQid" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="internalTrial.notifyColName" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="internalTrial.notifyColInstitute" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="internalTrial.notifyColEmail" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="internalTrial.notifyColForwardAddresses" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map((enrollment) => {
                    const { quipUser } = enrollment;
                    const qid = convertToQid(
                      quipUser.id,
                      quipUser.institute?.country || quipUser.userCountry,
                    );
                    const name = displayName(quipUser.firstname, quipUser.surname);
                    const forwards = formatForwardAddresses(quipUser.institute?.forwardAddresses);
                    return (
                      <TableRow key={enrollment.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(enrollment.id)}
                            onChange={() => toggleId(enrollment.id)}
                            inputProps={{
                              'aria-label': qid,
                            }}
                          />
                        </TableCell>
                        <TableCell>{qid}</TableCell>
                        <TableCell>{name || '—'}</TableCell>
                        <TableCell>{quipUser.institute?.name ?? '—'}</TableCell>
                        <TableCell>{quipUser.email}</TableCell>
                        <TableCell sx={{ maxWidth: 220, wordBreak: 'break-word' }}>
                          {forwards || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
        <Box display="flex" columnGap={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSend} disabled={loading || empty || selectedIds.size === 0}>
            <FormattedMessage id="internalTrial.notifySendEmail" />
          </Button>
          <Button variant="contained" color="secondary" onClick={closeDialog}>
            <FormattedMessage id="genericClose" />
          </Button>
        </Box>
      </ContentBox>
    </GenericDialog>
  );
}

export default injectIntl(RegisteredUsersNotifyDialog);
